import store from '../store/index';
import featureFlags from '@enums/feature-flags';
import LoginPage from '@/js/pages/login/login.vue';
import { isEmpty, get } from 'lodash';
import loadRoutes from '../utils/vue-plugins/loadRoutes';
import { isEmbedded } from '../webshell-embed/embedded';
import { createRouter, createWebHistory, createMemoryHistory } from 'vue-router';
import { MAINTENANCE_ROUTE_PATH, createMaintenanceRouteGuard } from './maintenance-guard';

const LandingPage = () => import('@/js/pages/landing-page/landing-page.vue');
const MaintenancePage = () => import('@/js/pages/maintenance/maintenance.vue');

const { canAccessLanding } = featureFlags;

const loginRoutes = ['/login', '/ow-auth/login', '/auth-saml/login', '/auth-openid/login'];

const publicRoutes = [...loginRoutes, MAINTENANCE_ROUTE_PATH];

// Embedded, the shell owns login, so any route to promo's own login is a bug.
const shellOwnsLogin = () => new Error('the shell owns login while embedded');

function disableLoginPagesWhenEmbedded(to) {
    if (loginRoutes.includes(to.path)) throw shellOwnsLogin();
    return true;
}

const routes = [
    {
        path: '/login',
        name: 'login',
        id: 'login',
        component: LoginPage,
        meta: { hideNavbar: true },
        omitFromNav: true,
    },
    {
        path: '/',
        name: 'landing',
        featureToggle: 'canAccessLanding',
        id: 'landing',
        component: LandingPage,
        linkText: 'Landing',
        meta: { canAccess: canAccessLanding },
        index: 0,
        omitFromNav: true,
    },
    {
        path: MAINTENANCE_ROUTE_PATH,
        name: 'maintenance',
        id: 'maintenance',
        component: MaintenancePage,
        meta: { hideNavbar: true },
        omitFromNav: true,
    },
];

const navigationGuards = [
    createMaintenanceRouteGuard(() => store.state.context.isMaintenanceModeEnabled),
    // ensureAuthenticated
    to => {
        // If not authenticated, navigate to the login page
        const { profile, expireAt } = store.state.context;
        const isTokenExpired = expireAt < Date.now();

        if (profile && isTokenExpired) {
            // if auth token is expired a refresh should be requested
            store
                .dispatch('context/refreshUserContext')
                .catch(error => console.warn('[promo] token refresh failed', error));
        }

        if (!profile && !publicRoutes.includes(to.path)) {
            if (isEmbedded()) throw shellOwnsLogin();
            console.log('No valid user session, client forced redirect to /login page.');
            return { name: 'login' };
        }

        return true;
    },
    // ensureAuthorized
    to => {
        // Here we ensure a user has the required permissions to access the "to" page
        // iterate over all matched routes to check canAccess property
        const isAuthorized = to.matched.every(route =>
            route.meta.canAccess ? store.state.clientConfig.toggleLogic[route.meta.canAccess] : true
        );

        const clientConfigExists = !isEmpty(store.state.clientConfig.toggleLogic);

        if (!isAuthorized) {
            console.error('accessing page without required permissions');
            if (!clientConfigExists && isEmbedded()) throw shellOwnsLogin();
            // If clientConfig does not exist then it means we have cleared out vuex and will need to login
            const path = clientConfigExists ? '/error/403' : '/login';
            return { path };
        }

        return true;
    },
    // maintainCurrentTenantInQuery
    to => {
        // Context is the source of truth for the current tenant; this only mirrors it into the
        // URL so links remain shareable/bookmarkable. The query string is never read back to
        // change the tenant during normal navigation.
        //
        // Embedded, the tenant is a segment of the shell's URL, managed by the webshell.
        if (!isEmbedded() && store.getters['context/isMultitenancyEnabled']) {
            const currentTenant = store.state.context.currentTenant;
            if (currentTenant && to.query.tenant !== currentTenant) {
                return { ...to, query: { ...to.query, tenant: currentTenant }, replace: true };
            }
        }
        return true;
    },
];
// Import routes and build router
const loadedRoutes = loadRoutes(import.meta.glob(['../pages/**/*.router.js'], { eager: true }));
const filteredRoutes = loadedRoutes.filter(route => {
    if (!route.featureToggle) return true;
    return get(store.state.clientConfig.toggleLogic, route.featureToggle, true);
});

// `base` is the shell prefix promo is mounted under when embedded (e.g. `/acme/promo`), or the
// standalone deploy sub-path otherwise.
export default function createAppRouter(
    base = import.meta.env.BASE_URL,
    { embedded = false } = {}
) {
    const router = createRouter({
        // Embedded, promo shares a document with the shell's own router. Two createWebHistory
        // routers overwrite each other's `history.state`, so promo keeps its routing in memory and
        // tells the shell where it is via appRouter.reportCurrentRoute from create-app. The base
        // only shapes `href`s, so links and new tabs open the shell URL.
        history: embedded ? createMemoryHistory(base) : createWebHistory(base),
        routes: [...routes, ...filteredRoutes],
    });

    if (embedded) router.beforeEach(disableLoginPagesWhenEmbedded);

    router.beforeEach((to, from, next) => {
        store.commit('setIsNavigating', true);
        return next();
    });

    navigationGuards.forEach(guard => router.beforeEach(guard));

    router.afterEach(() => {
        store.commit('setIsNavigating', false);
    });

    return router;
}
